import { API_BASE_URL } from '../config';
import React, { useEffect, useMemo, useState } from 'react';
import { slotRange } from './AppointmentCalendar';

const HOUR_PX = 52;
const DAY_START = 8 * 60;
const DAY_END = 20 * 60;

const BOOKING_COLOR = {
  Planned: '#0d6efd',
  Scheduled: '#198754',
  Completed: '#6c757d',
  Overdue: '#dc3545'
};

const ROOM_STATUS_COLOR = {
  Available: '#198754',
  Occupied: '#fd7e14',
  'Under Maintenance': '#6c757d'
};

function pad(value) {
  return String(value).padStart(2, '0');
}

function dayKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function keyFromValue(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return dayKey(date);
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfDay(next);
}

function startOfWeek(date) {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() + 6) % 7));
}

function sameDay(left, right) {
  return dayKey(left) === dayKey(right);
}

function clockLabel(minutes) {
  const hour = Math.floor(minutes / 60);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12} ${suffix}`;
}

function layoutEvents(events) {
  const items = events.map((event) => ({ ...event })).sort((left, right) => left.start - right.start || left.end - right.end);
  const clusters = [];
  items.forEach((event) => {
    let cluster = clusters.find((group) => group.end > event.start);
    if (!cluster) {
      cluster = { end: event.end, items: [] };
      clusters.push(cluster);
    }
    cluster.end = Math.max(cluster.end, event.end);
    const used = new Set(cluster.items.filter((item) => item.end > event.start).map((item) => item.column));
    let column = 0;
    while (used.has(column)) column += 1;
    event.column = column;
    cluster.items.push(event);
  });
  clusters.forEach((cluster) => {
    const columns = Math.max(...cluster.items.map((item) => item.column)) + 1;
    cluster.items.forEach((item) => {
      item.columns = columns;
    });
  });
  return items;
}

function therapistName(plan) {
  return plan.THERAPIST_NAME || plan.THERAPIST_ID?.THERAPIST_NAME || '';
}

function RoomCalendar() {
  const [view, setView] = useState('daily');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [rooms, setRooms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const today = startOfDay(new Date());
  const rangeStart = view === 'daily' ? startOfDay(anchor) : startOfWeek(anchor);
  const days = Array.from({ length: view === 'daily' ? 1 : 7 }, (_, index) => addDays(rangeStart, index));
  const start = dayKey(days[0]);
  const end = dayKey(days[days.length - 1]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Sign in again to view the room calendar.');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/procedureRooms/schedule?start=${start}&end=${end}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not load the room calendar.');
        if (!cancelled) {
          setRooms(Array.isArray(data.rooms) ? data.rooms : []);
          setPlans(Array.isArray(data.plans) ? data.plans : []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [start, end]);

  const selectedRoom = rooms.find((room) => room._id === selectedRoomId) || null;

  const columns = useMemo(() => {
    if (view === 'daily') {
      return rooms.map((room) => ({
        key: room._id,
        room,
        day: days[0],
        heading: room.ROOM_NAME,
        subheading: room.STATUS
      }));
    }
    return days.map((day) => ({
      key: dayKey(day),
      room: selectedRoom,
      day,
      heading: day.toLocaleDateString(undefined, { weekday: 'short' }),
      subheading: String(day.getDate())
    }));
  }, [view, rooms, days, selectedRoom]);

  const placed = useMemo(() => {
    const byColumn = {};
    const untimed = {};
    columns.forEach((column) => {
      byColumn[column.key] = [];
      untimed[column.key] = [];
    });
    plans.forEach((plan) => {
      const column = columns.find((item) => {
        if (keyFromValue(plan.PROCEDURE_DATE) !== dayKey(item.day)) return false;
        if (item.room && String(plan.ROOM_ID?._id || plan.ROOM_ID) !== String(item.room._id)) return false;
        return true;
      });
      if (!column) return;
      const slot = slotRange(plan.PROCEDURE_SLOT);
      const event = { plan };
      if (!slot) untimed[column.key].push(event);
      else byColumn[column.key].push({ ...event, start: slot.start, end: slot.end });
    });
    Object.keys(byColumn).forEach((key) => {
      byColumn[key] = layoutEvents(byColumn[key]);
    });
    return { byColumn, untimed };
  }, [plans, columns]);

  const timed = Object.values(placed.byColumn).flat();
  const first = timed.length ? Math.min(...timed.map((item) => item.start)) : DAY_START;
  const last = timed.length ? Math.max(...timed.map((item) => item.end)) : DAY_END;
  const startMinute = Math.min(DAY_START, Math.floor(first / 60) * 60);
  const endMinute = Math.max(DAY_END, Math.ceil(last / 60) * 60);
  const hours = [];
  for (let minute = startMinute; minute < endMinute; minute += 60) hours.push(minute);
  const gridHeight = hours.length * HOUR_PX;
  const now = new Date();
  const nowMinute = now.getHours() * 60 + now.getMinutes();
  const showNow = nowMinute >= startMinute && nowMinute <= endMinute && days.some((day) => sameDay(day, today));
  const title = view === 'daily'
    ? anchor.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : `${days[0].toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${days[6].toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
      <h3 style={{ marginTop: 0 }}>Room calendar</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
        <button type="button" onClick={() => setAnchor(today)} style={toolbarButton}>Today</button>
        <button type="button" aria-label="Previous" onClick={() => setAnchor(addDays(anchor, view === 'daily' ? -1 : -7))} style={toolbarButton}>‹</button>
        <button type="button" aria-label="Next" onClick={() => setAnchor(addDays(anchor, view === 'daily' ? 1 : 7))} style={toolbarButton}>›</button>
        <strong style={{ marginRight: 'auto' }}>{title}</strong>
        {['daily', 'weekly'].map((option) => (
          <button key={option} type="button" onClick={() => setView(option)} style={{ ...toolbarButton, backgroundColor: view === option ? '#007bff' : 'white', color: view === option ? 'white' : '#007bff' }}>
            {option === 'daily' ? 'Daily' : 'Weekly'}
          </button>
        ))}
      </div>
      {loading && <p>Loading rooms...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!loading && !error && rooms.length === 0 && <p>No rooms have been added yet.</p>}
      {!loading && !error && rooms.length > 0 && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 280px', maxWidth: '100%' }}>
            <button
              type="button"
              onClick={() => setSelectedRoomId('')}
              style={roomCardStyle(!selectedRoomId)}
            >
              <strong>All rooms</strong>
              <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>{rooms.length} rooms</div>
            </button>
            {rooms.map((room) => (
              <button key={room._id} type="button" onClick={() => setSelectedRoomId(room._id)} style={roomCardStyle(selectedRoomId === room._id)}>
                <strong>{room.ROOM_NAME}</strong>
                <div style={{ color: ROOM_STATUS_COLOR[room.STATUS] || '#495057', fontSize: '0.85rem' }}>{room.STATUS} · Capacity {room.CAPACITY}</div>
                {selectedRoomId === room._id && (
                  <div style={{ marginTop: '0.45rem', color: '#495057', fontSize: '0.85rem', textAlign: 'left' }}>
                    <div>Location: {room.LOCATION || '—'}</div>
                    <div>Equipment: {(room.EQUIPMENT_AVAILABLE || []).join(', ') || '—'}</div>
                    <div>Room ID: {room.ROOM_ID}</div>
                  </div>
                )}
              </button>
            ))}
          </div>
          <div style={{ flex: '1 1 520px', minWidth: 0, border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'auto', maxHeight: 680, backgroundColor: 'white' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `72px repeat(${Math.max(columns.length, 1)}, minmax(150px, 1fr))`, minWidth: 72 + Math.max(columns.length, 1) * 150, position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'white', borderBottom: '1px solid #dee2e6' }}>
              <div />
              {columns.map((column) => (
                <button
                  key={column.key}
                  type="button"
                  onClick={() => column.room && setSelectedRoomId(column.room._id)}
                  style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #f1f3f5', borderBottom: 'none', borderTop: 'none', borderRight: 'none', background: selectedRoomId && column.room && column.room._id === selectedRoomId ? '#e7f5f2' : 'white', cursor: column.room ? 'pointer' : 'default' }}
                >
                  <div style={{ fontWeight: 700 }}>{column.heading}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{column.subheading}</div>
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `72px repeat(${Math.max(columns.length, 1)}, minmax(150px, 1fr))`, minWidth: 72 + Math.max(columns.length, 1) * 150 }}>
              <div />
              {columns.map((column) => (
                <div key={`${column.key}-untimed`} style={{ minHeight: 28, borderLeft: '1px solid #f1f3f5', padding: 4 }}>
                  {placed.untimed[column.key].map((item) => (
                    <BookingBlock key={item.plan._id} plan={item.plan} showRoom={view === 'weekly' && !selectedRoom} />
                  ))}
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `72px repeat(${Math.max(columns.length, 1)}, minmax(150px, 1fr))`, minWidth: 72 + Math.max(columns.length, 1) * 150 }}>
              <div>
                {hours.map((minute) => (
                  <div key={minute} style={{ height: HOUR_PX, textAlign: 'right', paddingRight: 8, color: '#6c757d', fontSize: '0.75rem' }}>{clockLabel(minute)}</div>
                ))}
              </div>
              {columns.map((column) => (
                <div key={column.key} style={{ position: 'relative', height: gridHeight, borderLeft: '1px solid #f1f3f5', backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${HOUR_PX - 1}px, #e9ecef ${HOUR_PX - 1}px, #e9ecef ${HOUR_PX}px)` }}>
                  {showNow && sameDay(column.day, today) && (
                    <div style={{ position: 'absolute', top: ((nowMinute - startMinute) / 60) * HOUR_PX, left: 0, right: 0, borderTop: '2px solid #dc3545', zIndex: 1, pointerEvents: 'none' }} />
                  )}
                  {placed.byColumn[column.key].map((item) => {
                    const top = ((item.start - startMinute) / 60) * HOUR_PX;
                    const height = Math.max(((item.end - item.start) / 60) * HOUR_PX, 28);
                    const width = `calc(${100 / item.columns}% - 4px)`;
                    const left = `calc(${(100 / item.columns) * item.column}% + 2px)`;
                    return (
                      <BookingBlock
                        key={item.plan._id}
                        plan={item.plan}
                        showRoom={view === 'weekly' && !selectedRoom}
                        style={{ position: 'absolute', top, height, width, left, overflow: 'hidden' }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BookingBlock({ plan, showRoom, style }) {
  const color = BOOKING_COLOR[plan.PROCEDURE_STATUS] || '#0f766e';
  const patient = plan.PATIENT_ID?.PATIENT_NAME || 'Patient';
  const sitting = plan.SESSION_TOTAL > 1 ? ` · Sitting ${plan.SESSION_NUMBER || 1} of ${plan.SESSION_TOTAL}` : '';
  const room = showRoom ? plan.ROOM_NAME : '';
  return (
    <div
      title={[patient, plan.PROCEDURE_NAME, plan.PROCEDURE_SLOT, therapistName(plan), plan.ROOM_NAME, plan.PROCEDURE_STATUS].filter(Boolean).join(' · ')}
      style={{
        display: 'block',
        textAlign: 'left',
        borderLeft: `3px solid ${color}`,
        backgroundColor: `${color}22`,
        borderRadius: 4,
        padding: '2px 6px',
        fontSize: '0.78rem',
        lineHeight: 1.25,
        marginBottom: 4,
        ...style
      }}
    >
      <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{patient}</strong>
      <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {[plan.PROCEDURE_NAME, plan.PROCEDURE_SLOT, room].filter(Boolean).join(' · ')}{sitting}
      </span>
    </div>
  );
}

function roomCardStyle(active) {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    marginBottom: '0.5rem',
    padding: '0.75rem',
    borderRadius: '8px',
    border: active ? '2px solid #1f4d40' : '1px solid #dee2e6',
    backgroundColor: active ? '#e7f5f2' : 'white',
    cursor: 'pointer'
  };
}

const toolbarButton = {
  padding: '6px 12px',
  borderRadius: 4,
  border: '1px solid #007bff',
  backgroundColor: 'white',
  color: '#007bff',
  cursor: 'pointer',
  fontWeight: 600
};

export default RoomCalendar;
